import { ManufacturerProductPropertyOptionDetail } from '@deltek/specification-client/core/Models/product'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Active, DragOverEvent, DragStartEvent, Over } from '@dnd-kit/core'
import { Theme, Typography, makeStyles } from '@material-ui/core'
import SortableComponent from 'bpm/components/sortable/SortableComponent'
import clsx from 'clsx'
import React, {
    Dispatch,
    FC,
    Fragment,
    KeyboardEvent,
    MutableRefObject,
    SetStateAction,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import usePreventClickOnDoubleClick from 'bpm/shared/hooks/usePreventClickOnDoubleClick'

import { processEnv } from 'components/configuration/environmentVariables'
import Option from '../../Option'
import { AFFIX_LIMIT } from '../NonMasterSpec/editNonMS/NonMSEditableOptionGroup'
import { NonMSAction } from '../NonMasterSpec/nonMSTypes'
import { PPL_ACTIONS } from '../PPLProvider'
import { ERROR_DUPLICATE_OPTION, MANUFACTURER_OPTION } from '../constants'
import { checkForDuplicates } from '../utils/optionUtils'
import DndOption from './DndOption'
import OptionInput from './OptionInput'
import UnitsOfMeasurement from './UnitsOfMeasurement'

const useStyles = makeStyles((theme: Theme) => ({
    editable: {
        fontSize: pxToRem(13),
        wordBreak: 'break-word',
        alignItems: 'center',
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
        border: 'none',
        outline: 'none',
        '&:hover': {
            cursor: 'text',
        },
    },
    spacer: {
        paddingRight: theme.spacing(1.75),
    },
    optionSpacer: {
        paddingLeft: theme.spacing(1),
    },
    optionWrapper: {
        display: 'inline-flex',
        alignItems: 'center',
    },
}))

interface DndPropertiesProps {
    isAddActive?: boolean
    dispatch?: Dispatch<NonMSAction>
    setIsAddActive?: Dispatch<SetStateAction<boolean>>
    propertyName: string
    subPropertyName: string
    prefixEditable?: boolean
    premiumOptions: Array<Partial<ManufacturerProductPropertyOptionDetail>>
    determineSelected: (value: string, optionId: string) => boolean
    onDragStart?: (event: DragStartEvent) => void
    onDragEnd?: (active: Active, over: Over) => void
    onRemoveClick: (id: string) => void
    onSelectOption: (
        id: string,
        propertyName: string,
        subPropertyName: string
    ) => void
    onOptionBlur?: (id: string, value: string) => void
    onUOMBlur?: (property: any) => void
    setInitialState?: () => void
    handleInsertPremiumOption?: (
        property: string | Record<string, string>
    ) => boolean
    handlePrefixChange?: (text: string, optionIndex: number) => void
}

const DndProperties: FC<DndPropertiesProps> = ({
    isAddActive,
    dispatch,
    setIsAddActive,
    propertyName,
    subPropertyName,
    prefixEditable = false,
    premiumOptions,
    determineSelected,
    onDragEnd,
    onRemoveClick,
    onSelectOption,
    onOptionBlur,
    onUOMBlur,
    setInitialState,
    handleInsertPremiumOption,
    handlePrefixChange,
}) => {
    const classes = useStyles()

    const [error, setError] = useState<string | Record<string, string>>('')
    const [isUOMActive, setIsUOMActive] = useState(false)
    const [editableOption, setEditableOption] = useState('')
    const [tempValue, setTempValue] = useState('')

    const keyCombiInitRef: MutableRefObject<boolean> = useRef(false)

    const sortableItems = useMemo(
        () =>
            premiumOptions.map(
                (
                    item: Partial<ManufacturerProductPropertyOptionDetail>,
                    index
                ) => item.optionId ?? index
            ),
        [premiumOptions]
    )

    const noop = useCallback(() => {}, [])

    const insertPremiumOption = useCallback(
        (e) => {
            if (e.target.value) {
                const property = e.target.value.trim()
                if (property && handleInsertPremiumOption) {
                    const hasError = handleInsertPremiumOption(property)

                    if (hasError) {
                        setError(ERROR_DUPLICATE_OPTION)
                        return
                    }
                }
            }

            setIsAddActive?.(false)
            dispatch?.({
                type: PPL_ACTIONS.setOptionActive,
                payload: '',
            } as NonMSAction)
        },
        [handleInsertPremiumOption, setIsAddActive, dispatch]
    )

    const handleDragEnd = useCallback(
        (drag: DragOverEvent) => {
            const { active, over } = drag
            if (!!active && !!over && active.id !== over.id && !!onDragEnd) {
                onDragEnd(active, over)
            }
        },
        [onDragEnd]
    )

    const handleRemoveClick = useCallback(
        (id: string) => {
            // TODO: Junar Implement delete API call here
            onRemoveClick?.(id)
        },
        [onRemoveClick]
    )

    const handleSetSelected = useCallback(
        (id: string) => {
            onSelectOption?.(id, propertyName, subPropertyName)
        },
        [propertyName, subPropertyName, onSelectOption]
    )

    const handleAdd = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (error) {
                setError('')
            }

            if (e.ctrlKey && e.key === '<') {
                if (!keyCombiInitRef.current) {
                    keyCombiInitRef.current = true
                } else {
                    setIsUOMActive(true)
                }
                return
            }

            if (!!keyCombiInitRef.current) {
                keyCombiInitRef.current = false
            }

            if (['Enter', 'Tab'].includes(e.key)) {
                insertPremiumOption(e)
            }
        },
        [error, insertPremiumOption]
    )

    const handlePrefixBlur = useCallback(
        (index) => (
            e: any //set this as any since the span is contenteditable
        ) => {
            if (!!e.target?.innerText) {
                handlePrefixChange?.(e.target?.innerText, index)
            }
        },
        [handlePrefixChange]
    )

    const ChildRenderer = memo(
        ({
            value,
            optionId,
            activeId,
            index = 0,
            isInlineTextOnly,
            ...props
        }: Partial<ManufacturerProductPropertyOptionDetail> & {
            activeId?: string
            index?: number
            isInlineTextOnly?: boolean
        }) => {
            let optionValue = value
            let optionIdentifier: string = optionId ?? ''
            let optionIndex = index

            if (!optionValue && !optionIdentifier && !!activeId) {
                optionIndex = premiumOptions.findIndex(
                    (option) => option.optionId === activeId
                )
                optionValue = premiumOptions[optionIndex].value
                optionIdentifier = activeId
            }

            if (!optionValue || !optionIdentifier) {
                return <></>
            }

            const isSelected = determineSelected(
                optionValue ?? '',
                optionIdentifier
            )

            const handleOptionClick = useCallback(() => {
                handleSetSelected(optionIdentifier)
            }, [optionIdentifier, handleSetSelected])

            const handleOptionDoubleClick = useCallback(() => {
                if (optionValue && typeof optionValue === 'string') {
                    setTempValue(optionValue)
                }
                setEditableOption(optionIdentifier)
            }, [optionIdentifier])

            const handleOptionBlur = useCallback(
                (e) => {
                    if (
                        e.target.value === optionValue ||
                        (!e.target.value && !!tempValue)
                    ) {
                        setTempValue('')
                        setEditableOption('')
                        return
                    }

                    const hasDuplicates = checkForDuplicates({
                        property: e.target.value,
                        premiumOptions,
                    })

                    if (hasDuplicates && e.target.value !== optionValue) {
                        setTempValue(e.target.value)
                        setError(ERROR_DUPLICATE_OPTION)
                        return
                    }

                    onOptionBlur?.(optionIdentifier ?? '', e.target.value)
                    setTempValue('')
                    setEditableOption('')
                    setError('')
                },
                [onOptionBlur, optionIdentifier, checkForDuplicates, setError]
            )

            const handleUOMBlur = useCallback(
                (property) => {
                    if (
                        optionValue ===
                            `${property.imperialUnits} (${property.metricUnits})` ||
                        (!!optionValue &&
                            !property.imperialUnits &&
                            !property.metricUnits)
                    ) {
                        setEditableOption('')
                        return false
                    }

                    const hasDuplicates = checkForDuplicates({
                        property,
                        premiumOptions,
                    })

                    if (hasDuplicates) {
                        return hasDuplicates
                    }

                    onUOMBlur?.({
                        ...props,
                        ...property,
                        optionId,
                        value: `${property.imperialUnits} (${property.metricUnits})`,
                    })

                    setEditableOption('')
                    setError('')

                    return false
                },
                [onUOMBlur]
            )

            const handleCancel = useCallback(() => {
                setError('')
                setTempValue('')
                setEditableOption('')
                setIsAddActive?.(false)
            }, [setIsAddActive])

            const [
                handleClick,
                handleDoubleClick,
            ] = usePreventClickOnDoubleClick(
                handleOptionClick,
                handleOptionDoubleClick
            )

            return (
                <Typography
                    component="span"
                    data-testid="optionWrapper"
                    key={optionIdentifier}
                    role="button"
                    tabIndex={0}
                    className={classes.optionWrapper}
                    onClick={!!editableOption ? noop : handleClick}
                    onDoubleClick={!!editableOption ? noop : handleDoubleClick}
                >
                    {editableOption === optionIdentifier &&
                    props.isUnitOfMeasure ? (
                        <Typography className={classes.spacer} component="span">
                            <UnitsOfMeasurement
                                error={typeof error === 'string' ? {} : error}
                                setError={setError}
                                handleInsertPremiumOption={handleUOMBlur}
                                setIsUOMActive={setIsUOMActive}
                                metricValue={props.metricUnits}
                                imperialValue={props.imperialUnits}
                                onCancel={handleCancel}
                            />
                        </Typography>
                    ) : editableOption === optionIdentifier &&
                      !props.isUnitOfMeasure ? (
                        <Typography className={classes.spacer} component="span">
                            <OptionInput
                                isTextOnly={
                                    prefixEditable &&
                                    processEnv.REACT_APP_EnableInlineTextOnly ===
                                        '1'
                                }
                                key={optionIdentifier}
                                onBlur={handleOptionBlur}
                                defaultValue={tempValue ?? optionValue}
                                error={error}
                                onCancel={handleCancel}
                            ></OptionInput>
                        </Typography>
                    ) : (
                        <DndOption
                            key={`${optionIdentifier}_${optionIndex}`}
                            id={optionIdentifier!}
                            onCloseClick={handleRemoveClick}
                            isSelected={isSelected}
                        >
                            <Option
                                key={optionIdentifier}
                                value={optionValue ?? ''}
                                optionId={optionIdentifier ?? ''}
                                propertyName={propertyName}
                                subPropertyName={subPropertyName}
                                isPremiumPlus
                                isInlineTextOnly={isInlineTextOnly}
                                isSelected={isSelected}
                                mode="premPlus"
                                tooltipText={
                                    isSelected
                                        ? `${MANUFACTURER_OPTION} selected`
                                        : MANUFACTURER_OPTION
                                }
                            />
                        </DndOption>
                    )}
                </Typography>
            )
        }
    )

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter' || e.code === 'Enter') {
                e.target.blur()
            } else if (
                e.target.innerText.length >= AFFIX_LIMIT &&
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.altKey
            ) {
                e.preventDefault()
                return
            }
        },
        [AFFIX_LIMIT]
    )

    useEffect(() => {
        if (isAddActive && !isUOMActive) {
            keyCombiInitRef.current = false
        }
    }, [isAddActive, isUOMActive])

    useEffect(() => {
        return () => {
            setError('')
            setIsUOMActive(false)
        }
    }, [setError, setIsUOMActive])

    useEffect(() => {
        if (setInitialState) {
            setInitialState()
        }
    }, [])

    return (
        <>
            <SortableComponent
                onDragEnd={handleDragEnd}
                sortableItems={sortableItems}
                ActiveComponent={ChildRenderer}
            >
                {premiumOptions.map(
                    (
                        {
                            optionId,
                            value,
                            ...props
                        }: Partial<ManufacturerProductPropertyOptionDetail>,
                        index
                    ) => {
                        return (
                            <Fragment key={`${optionId}_${index}`}>
                                {prefixEditable && index > 0 && (
                                    <Typography
                                        suppressContentEditableWarning={true}
                                        component="span"
                                        contentEditable={prefixEditable}
                                        tabIndex={prefixEditable ? 0 : 1}
                                        className={clsx({
                                            [classes.editable]: prefixEditable,
                                        })}
                                        onKeyDown={handleKeyDown}
                                        onBlur={handlePrefixBlur(index)}
                                    ></Typography>
                                )}
                                <ChildRenderer
                                    value={value}
                                    optionId={optionId}
                                    index={index}
                                    {...props}
                                />
                                <Typography
                                    className={clsx({
                                        [classes.spacer]: !prefixEditable,
                                    })}
                                    component="span"
                                ></Typography>
                            </Fragment>
                        )
                    }
                )}
            </SortableComponent>
            <Typography
                component="span"
                className={clsx({
                    [classes.optionSpacer]: prefixEditable,
                })}
            >
                {!!isAddActive &&
                    (!isUOMActive ? (
                        <OptionInput
                            error={error}
                            onBlur={insertPremiumOption}
                            onKeyDown={handleAdd}
                            isTextOnly={
                                prefixEditable &&
                                processEnv.REACT_APP_EnableInlineTextOnly ===
                                    '1'
                            }
                        />
                    ) : (
                        <UnitsOfMeasurement
                            error={typeof error === 'string' ? {} : error}
                            setError={setError}
                            handleInsertPremiumOption={
                                handleInsertPremiumOption
                            }
                            setIsUOMActive={setIsUOMActive}
                        />
                    ))}
            </Typography>
        </>
    )
}

export default DndProperties
